import React from 'react';
import styled from 'styled-components';
import { formatCustomizationMoney } from '../../api/customizationsAPI';
import AuthenticatedReferenceImage from './AuthenticatedReferenceImage';

const Grid = styled.div`
  display: grid;
  gap: 0.9rem;
`;
const Section = styled.section`
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.75rem;
  padding: 1.1rem;
`;
const Heading = styled.h3`
  color: var(--text-primary, #1e293b);
  font-size: 1rem;
  margin: 0 0 0.85rem;
`;
const Row = styled.div`
  margin-top: 0.7rem;
`;
const Label = styled.div`
  color: var(--text-secondary, #64748b);
  font-size: 0.75rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
`;
const Value = styled.div`
  color: var(--text-primary, #1e293b);
  line-height: 1.5;
  overflow-wrap: anywhere;
`;
const Colors = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.7rem;
`;
const Color = styled.span`
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.4rem;
  padding: 0.3rem 0.6rem;
`;
const Swatch = styled.span`
  background: ${(props) => props.$color};
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  height: 0.9rem;
  width: 0.9rem;
`;
const References = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(145px, 1fr));
`;
const ReferenceLink = styled.a`
  color: inherit;
  min-width: 0;
  text-decoration: none;
`;
const Filename = styled.div`
  color: var(--text-secondary, #64748b);
  font-size: 0.75rem;
  margin-top: 0.35rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const titleCase = (value) =>
  value ? String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'None';

const BriefRow = ({ label, value }) => (
  <Row>
    <Label>{label}</Label>
    <Value>{String(value || '').trim() || 'None'}</Value>
  </Row>
);

const PaintingBriefView = ({ request, compact = false }) => {
  const brief = request?.painting_brief || {};
  const references = request?.references || [];
  const size = `${brief.width_cm || '—'} × ${brief.height_cm || '—'} cm`;

  if (compact) {
    return (
      <Value>
        {size} · {titleCase(brief.creative_direction)} ·{' '}
        {formatCustomizationMoney(request?.budget_min_cents)} – {formatCustomizationMoney(request?.budget_max_cents)}
      </Value>
    );
  }

  return (
    <Grid>
      <Section>
        <Heading>Size</Heading>
        <Value>{size}</Value>
      </Section>
      <Section>
        <Heading>Vision</Heading>
        <BriefRow label="Creative direction" value={titleCase(brief.creative_direction)} />
        <BriefRow label="Description" value={brief.description} />
      </Section>
      <Section>
        <Heading>Palette and details</Heading>
        <BriefRow label="Color direction" value={titleCase(brief.color_preference)} />
        {brief.selected_colors?.length ? (
          <Colors>
            {brief.selected_colors.map((color) => (
              <Color key={color}><Swatch $color={color} />{color}</Color>
            ))}
          </Colors>
        ) : null}
        <BriefRow label="Subjects to include" value={brief.objects_or_subjects} />
        <BriefRow label="Text to include" value={brief.requested_text} />
      </Section>
      <Section>
        <Heading>References</Heading>
        {references.length ? (
          <References>
            {references.map((reference) => (
              <ReferenceLink
                key={reference.id}
                href={reference.url || undefined}
                target={reference.url ? '_blank' : undefined}
                rel="noreferrer"
              >
                <AuthenticatedReferenceImage
                  url={reference.url}
                  alt={reference.original_filename || 'Painting reference'}
                  aspect="4 / 3"
                  radius="0.5rem"
                />
                <Filename>{reference.original_filename || 'Reference image'}</Filename>
              </ReferenceLink>
            ))}
          </References>
        ) : <Value>No reference images</Value>}
      </Section>
      <Section>
        <Heading>Final touches</Heading>
        <BriefRow label="Special requests" value={request?.special_requests} />
        <BriefRow
          label="Budget"
          value={`${formatCustomizationMoney(request?.budget_min_cents)} – ${formatCustomizationMoney(request?.budget_max_cents)}`}
        />
      </Section>
    </Grid>
  );
};

export default PaintingBriefView;
